import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InfoOwners, Owners, Representative } from '../../interface/owners.interface';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ServiceInfoClientService } from '../../service/service-info-client.service';
import Swal from 'sweetalert2';
import { formatRut } from 'rutlib';
import { ConfigRutService } from 'src/app/services/config-rut.service';
import { catchError, debounceTime, of, distinctUntilChanged, filter, finalize, map, switchMap, Observable, tap } from 'rxjs';
import { InfoCompany } from '../../interface/company-client.interface';

@Component({
  selector: 'app-update-owners',
  templateUrl: './update-owners.component.html',
  styleUrls: ['./update-owners.component.scss'],
})
export class UpdateOwnersComponent {
  public isSearchingOwner: boolean = false;

  public isSearchFoundOwner: boolean = false;

  public isLoading: boolean = false;
  public rutUrl: number;
  public update: boolean;
  public hasOwners: boolean = false;
  closeModal: boolean;
  public relationType: string = "";
  public relationId: number = 0;
  public isNew: boolean = false;
  public name: string = "";
  public form = new FormGroup({
    fullRut: new FormControl<string | null>('', [Validators.required]),
    name: new FormControl<string | null>(null, [Validators.required]),
    civilState: new FormControl<string | null>(null),
    participation: new FormControl<number | null>(null),
    Dicom: new FormControl<number>(null),
    Sbif: new FormControl<number>(null),
    age: new FormControl<number>(null),
    isAval: new FormControl<boolean | null>(null),
    isRL: new FormControl<boolean | null>(null),
    role: new FormControl<string | null>(null),
    email: new FormControl<string | null>(null, [Validators.email]),
    phone: new FormControl<string | null>(null),
    description: new FormControl<string | null>(null),
    deedDocumentType: new FormControl<string | null>(null),
    deedDocumentInfo: new FormGroup({
      notaryName: new FormControl<string | null>(null),
      notaryAddress: new FormControl<string | null>(null),
      deedDate: new FormControl<string | null>(null),
      deedCode: new FormControl<string | null>(null)
    })
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<UpdateOwnersComponent>,
    private configuracionRut: ConfigRutService,
    private _userService: ServiceInfoClientService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.listenDeedDocumentTypeChanges();
    this.setForm(this.data);
    this.listenRutChanges();
    this.relationType = this.data.relationType;
    this.relationId = this.data.relationId;
    this.isNew = this.data.isNew;
    this.name = this.data.completeName != undefined ? this.data.completeName : "nuevo";
    this.route.queryParams.subscribe((params: any) => {
      const { client } = params;
      this.rutUrl = parseInt(client);
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  get f() {
    return this.form.controls;
  }

  private listenRutChanges() {
    this.form.get('fullRut')?.valueChanges
    .pipe(
      debounceTime(800), //Delay de 800ms
      distinctUntilChanged(), // Evitar que se llame al subscribe si el rut no cambia
      tap((rut) => {
        if(!this.configuracionRut.isRutValid(rut)){
          this.resetOwnerFields();
        }
      }),
      filter((rut) => this.configuracionRut.isRutValid(rut)), // Subscribe solo si el rut es valido
      map((rut) => {
        const unformattedRut = rut.replace(/\./g, '').replace(/-/g, '');
        return unformattedRut.substring(0, unformattedRut.length - 1);
      }),
      map((rut) => Number(rut)),
      switchMap((companyId) => this.requestOwnerInfo(companyId)), // Llamar al servicio para obtener la compañia por rut
    )
    .subscribe((companyInfo: InfoCompany | null) => {
      if(companyInfo){
        this.synchronizeOwnerFields(companyInfo);
      } else{
        this.resetOwnerFields();
      }
    });

  }

  private requestOwnerInfo(companyId: number): Observable<InfoCompany | null> {
    this.isSearchingOwner = true;
    return this._userService.infoCompany(companyId)
    .pipe(
      finalize(() => this.isSearchingOwner = false),
      catchError((err) => {
        return of(null); // Si no se encuentra la compañia, retornar null
      })
    );
  }

  private resetOwnerFields() {
    this.isSearchFoundOwner = false;
    this.form.patchValue({
      name: null,
      email: null,
    });
  }

  private synchronizeOwnerFields(clientInfo: InfoCompany){
    this.isSearchFoundOwner = true;
    this.form.patchValue({
      name: clientInfo.name,
      email: clientInfo.email, 
    });
  }

  private synchronizeRut(companyId: number) {
    this.isSearchingOwner = true;
    this._userService.infoCompany(companyId)
    .pipe(
      finalize(() => this.isSearchingOwner = false),
    )
    .subscribe({
      next: (res: InfoCompany) => {
        this.isSearchFoundOwner = true;
        this.form.patchValue({
          name: res.name,
          email: res.email, 
        });
      },
      error:(error) => {
        this.isSearchFoundOwner = false;
        this.form.patchValue({
          name: null,
          email: null,
        });
      }
    });
  }

  private listenDeedDocumentTypeChanges() {
    this.form.get('deedDocumentType')?.valueChanges.subscribe((deedDocumentType) => {
      if(deedDocumentType == 'Notaria'){
        //Enable required validations
        this.enableNotaryFields();
        this.disableSingleDayCompany();
        
      } else if(deedDocumentType == 'Empresa en un día'){
        this.disableNotaryFields();
        this.enableSingleDayCompany();
      } else {
        this.disableNotaryFields();
        this.disableSingleDayCompany();
        this.form.get('deedDocumentInfo.deedDate')?.clearValidators();
        this.form.get('deedDocumentInfo.deedDate')?.setValue(null);
      }
      //update validations
      this.form.get('deedDocumentInfo.notaryName')?.updateValueAndValidity();
      this.form.get('deedDocumentInfo.notaryAddress')?.updateValueAndValidity();
      this.form.get('deedDocumentInfo.deedDate')?.updateValueAndValidity();
      this.form.get('deedDocumentInfo.deedCode')?.updateValueAndValidity();
    });
  }

  /**
   * Habilitar los campos de notaria
   */
  private enableNotaryFields() {
    this.form.get('deedDocumentInfo.notaryName')?.setValidators([Validators.required]);
    this.form.get('deedDocumentInfo.notaryAddress')?.setValidators([Validators.required]);
    this.form.get('deedDocumentInfo.deedDate')?.setValidators([Validators.required]);
  }

  /**
   * Deshabilitar los campos de notaria
   */
  private disableNotaryFields() {
    this.form.get('deedDocumentInfo.notaryName')?.clearValidators();
    this.form.get('deedDocumentInfo.notaryAddress')?.clearValidators();
    this.form.patchValue({
      deedDocumentInfo:{        
        notaryName: null,
        notaryAddress: null,        
      }
    });
  }

  /**
   * Habilitar los campos de empresa en un día
   */
  private enableSingleDayCompany() {
    this.form.get('deedDocumentInfo.deedCode')?.setValidators([Validators.required]);
    this.form.get('deedDocumentInfo.deedDate')?.setValidators([Validators.required]);
  }

  /**
   * Deshabilitar los campos de empresa en un día
   */
  private disableSingleDayCompany(){
    this.form.get('deedDocumentInfo.deedCode')?.clearValidators();
    this.form.patchValue({
      deedDocumentInfo:{
        deedCode: null,
      }
    });
  }


  get deedDocumentType() {
    return this.form.get('deedDocumentType')?.value;
  }

  inputRut(event) {
    const { formattedRut, validation } = this.configuracionRut.changeRut(event);
    this.form.controls['fullRut'].setValue(formattedRut, { emitEvent: false });
    if (!validation) {
      this.form.controls['fullRut'].setErrors({
        invalidRut: false,
      });
    }
  }

  private setForm(owners: any | undefined) {
    this.hasOwners = owners != undefined;
    console.log(this.hasOwners);
    if (owners != undefined) {
      this.form.patchValue({
        fullRut: owners.fullRut,
        name: owners.completeName,
        participation: owners.participation,
        civilState: owners.civilState,
        age: owners.age,
        isAval: owners.isAval,
        isRL: owners.isRL,
        role: owners.role,
        email: owners.email,
        phone: owners.phone,
        description: owners.description,
        deedDocumentType: owners.deedDocument?.deedType,
        deedDocumentInfo: {
          notaryName: owners.deedDocument?.notaryName,
          notaryAddress: owners.deedDocument?.notaryAddress,
          deedDate: owners.deedDocument?.deedDate,
          deedCode: owners.deedDocument?.deedCode
        },
      });
    }
  }

  submitForm() {

    const deedDocumentType = this.form.get('deedDocumentType')?.value;
    const deedDocument = deedDocumentType 
    ? 
    {
      deedType: deedDocumentType,
      notaryName: this.form.get('deedDocumentInfo.notaryName')?.value,
      notaryAddress: this.form.get('deedDocumentInfo.notaryAddress')?.value,
      deedDate: this.form.get('deedDocumentInfo.deedDate')?.value,
      deedCode: this.form.get('deedDocumentInfo.deedCode')?.value
    }
    : null;


    // if (this.relationType == 'partner') {
    const createOwners: any = {
      rutClient: this.formatRutWithDV(this.rutUrl),
      rutOwner: this.form.get('fullRut')?.value,
      age: this.form.get('age')?.value,
      name: this.form.get('name')?.value,
      civilState: this.form.get('civilState')?.value,
      participation: this.form.get('participation')?.value,
      isAval: this.form.get('isAval')?.value ?? false,
      isRL: this.form.get('isRL')?.value ?? false,
      Dicom: this.form.get('Dicom')?.value,
      Sbif: this.form.get('Sbif')?.value,
      companyId: this.rutUrl,
      isOwner: true,
      completeName: this.form.get('name')?.value,
      email: this.form.get('email')?.value,
      phone: this.form.get('phone')?.value,
      role: this.form.get('role')?.value,
      description: this.form.get('description')?.value,
      relationId: this.relationId,
      relationType: this.relationType,
      deedDocument: deedDocument,
    };

    Swal.fire({
      title: this.isNew ? 'Guardando' : 'Actualizando',
      timerProgressBar: true,
      heightAuto: false,
      didOpen: () => {
        Swal.showLoading();
        this._userService.createOwner(createOwners).subscribe({
          next: (e) => {
            console.log(e);
            Swal.fire({
              title: this.isNew ? 'Creado correctamente' : 'Actualizado',
              icon: 'success',
              heightAuto: false,
            }).then((result) => {
              if (result.isConfirmed) {
                this.closeModal = true;
                this.dialogRef.close(this.closeModal);
              }
            });
          },
          error: (error) => {
            console.log(error);
            Swal.fire({
              title: error.error.message,
              text: 'No fue posible ' + (this.isNew ? 'crear' : 'actualizar') + ' la relación',
              icon: 'error',
              showConfirmButton: true,
              heightAuto: false,
              confirmButtonText: 'Cerrar',
            });
          },
        });
      },
    });
  }

  public formatRutWithDV(rutNum: number): string {
    const rut = rutNum.toString();
  
    let suma = 0;
    let multiplo = 2;
  
    for (let i = rut.length - 1; i >= 0; i--) {
      suma += parseInt(rut.charAt(i), 10) * multiplo;
      multiplo = multiplo === 7 ? 2 : multiplo + 1;
    }
  
    const dv = 11 - (suma % 11);
    let dvStr = dv === 11 ? '0' : dv === 10 ? 'K' : dv.toString();
  
    // Agregar puntos
    const rutWithDots = rut
      .split('')
      .reverse()
      .reduce((acc, val, i) => val + (i > 0 && i % 3 === 0 ? '.' + acc : acc), '');
  
    return `${rutWithDots}-${dvStr}`;
  }
  
  

}